import { useEffect, useState } from 'react';
import { useUserLanguage, useCreateOwnerContractContext } from '@/customHooks';
import { NormalFormDateField } from '@/components/ui/molecules/NormalFormFields';
import textsData from '@/textData/CreateContractsTexts.json';

export const SelectOwnerContractDatesMenu = () => {
  const { state, setDates, setButtonEnabled, setButtonDisabled } = useCreateOwnerContractContext();
  const userLanguage = useUserLanguage();
  
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const ownerContractFormTexts = texts.ownerContractForm;
  
  const [startDate, setStartDate] = useState<string>(state.startDate || '');
  const [finishDate, setFinishDate] = useState<string>(state.finishDate || '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateDates = () => {
      if (!startDate || !finishDate) {
        setButtonDisabled();
        setError(null);
        return;
      }

      const start = new Date(startDate);
      const finish = new Date(finishDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const constraint2025 = new Date('2025-01-01');

      if (start < constraint2025) {
        setButtonDisabled();
        setError(userLanguage === 'es' 
          ? 'La fecha de inicio no puede ser anterior al 01-01-2025' 
          : 'Start date cannot be before 2025-01-01');
        return;
      }

      if (finish < start) {
        setButtonDisabled();
        setError(userLanguage === 'es' 
          ? 'La fecha de finalización no puede ser anterior al inicio' 
          : 'Finish date cannot be before start date');
        return;
      }

      if (finish < today) {
        setButtonDisabled();
        setError(userLanguage === 'es' 
          ? 'La fecha de finalización no puede ser anterior al día actual' 
          : 'Finish date cannot be before today');
        return;
      }

      setError(null);
      setButtonEnabled();
    };

    validateDates();
    setDates(startDate, finishDate);
  }, [startDate, finishDate, setDates, setButtonEnabled, setButtonDisabled, userLanguage]);

  return (
    <div className="flex justify-center w-full">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold text-gray-900 border-b border-gray-100 pb-4">
            {userLanguage === 'es' ? 'Seleccionar fechas del contrato' : 'Select contract dates'}
          </h2>
          <p className="text-sm text-gray-500">
            {userLanguage === 'es' 
              ? 'Defina el periodo de vigencia del contrato entre el manager y el propietario.' 
              : 'Define the duration of the contract between the manager and the owner.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NormalFormDateField
            label={ownerContractFormTexts.startDateLabel}
            name="startDate"
            value={startDate}
            onChange={(val) => setStartDate(val || '')}
            required
          />
          <NormalFormDateField
            label={ownerContractFormTexts.finishDateLabel}
            name="finishDate"
            value={finishDate}
            onChange={(val) => setFinishDate(val || '')}
            required
          />
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2 text-sm text-red-600 animate-in fade-in slide-in-from-top-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};
